import React, { useContext, useEffect, useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Select,
  Card,
  Button,
  Row,
  Col,
  InputNumber,
  message,
} from 'antd';
import LanguageList from '../../components/language-list';
import { Context } from '../../context/context';
import { useNavigate, useParams } from 'react-router-dom';
import couponService from '../../services/coupon';
import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { removeFromMenu } from '../../redux/slices/menu';
import { useTranslation } from 'react-i18next';

const CouponAdd = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { defaultLang, setDefaultLang } = useContext(Context);
  const [names, setNames] = useState({});
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [shops, setShops] = useState([]);
  const [couponType, setCouponType] = useState('fix');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { activeMenu } = useSelector((state) => state.menu, shallowEqual);

  // Expanded Operator Categories
  const OPERATOR_CATEGORIES = [
    { value: 'All', label: 'All' },
    { value: 'Prepaid', label: 'Prepaid' },
    { value: 'DTH', label: 'DTH' },
    { value: 'Electricity', label: 'Electricity' },
    { value: 'Broadband', label: 'Broadband' },
    { value: 'Gas - Piped Gas', label: 'Gas - Piped Gas' },
    { value: 'Fastag', label: 'Fastag' },
    { value: 'Landline', label: 'Landline' },
    { value: 'Postpaid', label: 'Postpaid' },
    { value: 'Broadband Postpaid', label: 'Broadband Postpaid' },
    { value: 'Insurance', label: 'Insurance' },
    { value: 'EMI Payment', label: 'EMI Payment' },
    { value: 'LPG Booking', label: 'LPG Booking' },
  ];

  // Fetch shops
  const fetchShops = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/shops`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      if (result.status && result.data) {
        setShops(result.data);
      }
    } catch (error) {
      message.error(t('error_fetching_shops'));
    }
  };

  // Fetch coupon details for editing
  async function fetchCoupon() {
    setLoading(true);
    try {
      const { data } = await couponService.getById(id);
      setData(data);
      setCouponType(data.type);
      const namesArray = { ...names };
      data.translations?.forEach((item) => {
        namesArray[item.locale] = item.title;
      });
      form.setFieldsValue({
        name: data.name,
        shop_id: data.shop_id,
        type: data.type,
        description: data.description,
        qty: data.qty,
        min_avail_val: data.min_avail_amount ?? data.min_avail_val,
        cat: data.cat,
        [data.type === 'fix' ? 'price' : 'percent']: data.price,
        expired_at: moment(data.expired_at, 'YYYY-MM-DD'),
        status: data.status || 'active',
        max_uses_per_user: data.max_uses_per_user ?? 1,
        max_discount_amount: data.max_discount_amount,
      });
      setNames(namesArray);
    } catch (error) {
      message.error(t('error_fetching_coupon'));
    } finally {
      setLoading(false);
    }
  }

  // Language change handler
  const onChangeLanguage = (value) => {
    setDefaultLang(value);
    form.setFieldsValue({
      title: names[value],
    });
  };

  // Coupon type change handler
  const handleTypeChange = (value) => {
    setCouponType(value);
    // Clear the price/percent field when switching types
    form.setFieldsValue({
      price: undefined,
      percent: undefined,
    });
  };

  // Form submission handler
  const onFinish = async (values) => {
    setLoadingBtn(true);
    const params = {
      ...values,
      expired_at: moment(values.expired_at).format('YYYY-MM-DD'),
      qty: Number(values.qty),
      cat: values.cat, // Send category name directly
      min_avail_val: values.min_avail_val ? Number(values.min_avail_val) : 0,
      status: values.status || 'active',
      max_uses_per_user: values.max_uses_per_user
        ? Number(values.max_uses_per_user)
        : 1,
      max_discount_amount:
        values.max_discount_amount !== undefined && values.max_discount_amount !== null
          ? Number(values.max_discount_amount)
          : null,
    };

    if (values.type === 'fix') {
      params.price = Number(values.price);
      delete params.percent;
    } else {
      params.percent = Number(values.percent);
      delete params.price;
    }

    try {
      if (id) {
        await couponService.update(id, params);
        message.success(t('coupon_updated_successfully'));
      } else {
        await couponService.create(params);
        message.success(t('coupon_created_successfully'));
      }
      const nextUrl = 'coupons';
      dispatch(removeFromMenu({ ...activeMenu, nextUrl }));
      navigate(`/${nextUrl}`);
    } catch (error) {
      if (error.response?.data?.errors) {
        // Handle validation errors
        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          message.error(`${key}: ${errors[key][0]}`);
        });
      } else if (error.response?.data?.message) {
        // Handle specific error message from API
        message.error(error.response.data.message);
      } else {
        // Handle generic error
        message.error(t('error_saving_coupon'));
      }
    } finally {
      setLoadingBtn(false);
    }
  };

  // Form validation failure handler
  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
    message.error(t('Please fill all required fields'));
  };

  // Effect to fetch shops and coupon details
  useEffect(() => {
    fetchShops();
    if (id) {
      fetchCoupon();
    }
  }, [id]);

  return (
    <>
      <style>{`
        .coupon-form .ant-form-item {
          display: flex !important;
          flex-direction: column !important;
        }
        .coupon-form .ant-form-item .ant-form-item-label {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          text-align: left !important;
          padding: 0 0 8px !important;
          white-space: normal !important;
          flex: none !important;
        }
        .coupon-form .ant-form-item .ant-form-item-control {
          flex: 1 !important;
          max-width: 100% !important;
        }
      `}</style>
      <Card
        title={id ? t('edit.coupon') : t('add.coupon')}
        extra={
          <LanguageList value={defaultLang} onChangeLanguage={onChangeLanguage} />
        }
        loading={loading}
      >
        <Form
          form={form}
          name='coupon-form'
          className='coupon-form'
          layout='vertical'
          initialValues={{
            status: 'active',
            max_uses_per_user: 1,
            type: 'fix',
            cat: 'All',
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          validateTrigger='onBlur'
        >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('Select shop')}
              name='shop_id'
              rules={[{ required: true, message: t('required') }]}
            >
              <Select
                placeholder={t('select_shop')}
                showSearch
                className='w-100'
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {shops.map((shop) => (
                  <Select.Option key={shop.id} value={shop.id}>
                    {shop.translation.title}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('name')}
              name='name'
              rules={[
                { required: true, message: t('required') },
                { min: 2, message: t('min_length_2') },
                { max: 50, message: t('max_length_50') },
              ]}
            >
              <Input className='w-100' />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('type')}
              name='type'
              rules={[{ required: true, message: t('required') }]}
            >
              <Select onChange={handleTypeChange} className='w-100'>
                <Select.Option value='fix'>{t('fix')}</Select.Option>
                <Select.Option value='percent'>{t('percent')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name='expired_at'
              label={t('expired.at')}
              rules={[{ required: true, message: t('required') }]}
            >
              <DatePicker
                className='w-100'
                disabledDate={(current) => moment().add(-1, 'days') >= current}
                format='YYYY-MM-DD'
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('quantity')}
              name='qty'
              rules={[
                { required: true, message: t('required') },
                { type: 'number', min: 1, message: t('min_quantity_1') },
              ]}
            >
              <InputNumber
                className='w-100'
                style={{ width: '100%' }}
                min={1}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Minimum Available Value'}
              name='min_avail_val'
              rules={[
                { required: true, message: t('required') },
                { type: 'number', min: 0, message: t('min_value_0') },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null) {
                      return Promise.reject(new Error(t('required')));
                    }
                    if (isNaN(value)) {
                      return Promise.reject(new Error(t('must_be_number')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              validateTrigger={['onChange', 'onBlur']}
            >
              <InputNumber
                className='w-100'
                style={{ width: '100%' }}
                min={0}
                step={1}
                placeholder={'Enter Minimum Value'}
                onChange={(value) => {
                  if (value === null) {
                    form.setFieldsValue({ min_avail_val: undefined });
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('category')}
              name='cat'
              rules={[{ required: true, message: t('required') }]}
            >
              <Select className='w-100'>
                {OPERATOR_CATEGORIES.map((category) => (
                  <Select.Option key={category.value} value={category.value}>
                    {t(category.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('description')}
              name='description'
              rules={[
                { required: true, message: t('required') },
                { max: 200, message: t('max_length_200') },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            {couponType === 'fix' ? (
              <Form.Item
                label={t('price')}
                name='price'
                rules={[
                  { required: true, message: t('required') },
                  { type: 'number', min: 0, message: t('min_price_0') },
                ]}
              >
                <InputNumber
                  className='w-100'
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                />
              </Form.Item>
            ) : (
              <Form.Item
                label={t('percent')}
                name='percent'
                rules={[
                  { required: true, message: t('required') },
                  { type: 'number', min: 1, message: t('min_percent_1') },
                  { type: 'number', max: 90, message: t('max_percent_90') },
                ]}
              >
                <InputNumber
                  className='w-100'
                  style={{ width: '100%' }}
                  min={1}
                  max={90}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace('%', '')}
                />
              </Form.Item>
            )}
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('status')}
              name='status'
              rules={[{ required: true, message: t('required') }]}
            >
              <Select className='w-100'>
                <Select.Option value='active'>{t('active')}</Select.Option>
                <Select.Option value='disabled'>{t('disabled')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label='Max uses per user'
              name='max_uses_per_user'
              rules={[
                { required: true, message: t('required') },
                { type: 'number', min: 1, message: t('min_value_1') },
              ]}
            >
              <InputNumber
                className='w-100'
                style={{ width: '100%' }}
                min={1}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label='Max discount amount'
              name='max_discount_amount'
              rules={[
                {
                  type: 'number',
                  min: 0,
                  message: t('min_value_0'),
                },
              ]}
            >
              <InputNumber
                className='w-100'
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                placeholder='Optional max discount'
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button type='primary' htmlType='submit' loading={loadingBtn}>
            {id ? t('update') : t('submit')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
    </>
  );
};

export default CouponAdd;
